import React from "react";
import {
  Zap, Gauge, FileSignature, ReceiptPoundSterling, MessagesSquare, Send,
  RefreshCw, FolderLock, ScrollText, Network,
} from "lucide-react";
import { Section, Reveal, Glass, Mono } from "./NetworkAtoms";

const ITEMS = [
  { Icon: Zap, title: "Auto-routing engine", body: "Rules decide which desks see a case — by amount band, sector, term, geography and appetite. Change a rule and the next decision follows it.", span: "lg:col-span-2", feature: true },
  { Icon: Gauge, title: "Lender fit-scoring", body: "Every case is scored against live criteria, so a lender can see why it reached them before they open it.", span: "lg:col-span-2", feature: true },
  { Icon: FileSignature, title: "E-signed agreements + mutual NDA", body: "Pre-signed by Connexis, countersigned online, versioned when terms change." },
  { Icon: ReceiptPoundSterling, title: "Automated commission statements", body: "Branded PDFs generated from live terms and emailed on schedule." },
  { Icon: MessagesSquare, title: "Real-time messaging", body: "Talk to the desk inside the portal. Reply straight from the alert email if that's easier." },
  { Icon: Send, title: "SMS + email workflows", body: "Invites, chases, offers, completions — sent, throttled and logged for you." },
  { Icon: RefreshCw, title: "CRM syncing", body: "Cases delivered into the lender's own CRM as well as the portal, with a delivery log." },
  { Icon: FolderLock, title: "Document vault", body: "Bank statements, accounts and agreements held per case, permissioned by role." },
  { Icon: ScrollText, title: "Audit logging", body: "Every status change, message and document event timestamped and exportable." },
  { Icon: Network, title: "Super-affiliate layers", body: "Introducers who recruit introducers earn an override, calculated automatically." },
];

export default function CapabilityGrid() {
  return (
    <Section eyebrow="Built, not bolted together" testId="network-capabilities"
             title="Everything here exists. None of it is a roadmap slide."
             lede="This is the actual system running the network today — the same one your cases move through.">
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {ITEMS.map((it, i) => (
          <Reveal key={it.title} delay={Math.min(i, 6) * 0.05} className={it.span || ""}>
            <Glass className={`h-full p-6 group relative overflow-hidden ${it.feature ? "bg-white/[0.05]" : ""}`}
                   testId={`capability-${i}`}>
              {it.feature && (
                <div aria-hidden
                     className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                     style={{ background: "linear-gradient(120deg, rgba(0,85,255,0.35), transparent 40%, transparent 60%, rgba(125,169,255,0.25))", mask: "linear-gradient(#000,#000) content-box, linear-gradient(#000,#000)", WebkitMaskComposite: "xor", maskComposite: "exclude", padding: 1 }} />
              )}
              <span className={`grid place-items-center size-10 rounded-xl border border-white/10 ${it.feature ? "bg-[#0055FF]/20 text-white" : "bg-white/[0.04] text-[#7da9ff]"}`}>
                <it.Icon className="size-[18px]" strokeWidth={1.7} />
              </span>
              <h3 className="mt-5 text-[15.5px] font-semibold text-white tracking-[-0.01em]">{it.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-400">{it.body}</p>
              {it.feature && <div className="mt-4"><Mono className="text-slate-600">Core engine</Mono></div>}
            </Glass>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
